import type { ForgeExecutionRuntime, ForgeRuntimeSurface, ForgeWorkSource } from "./runtimeSurface";

export type ForgeActionKind = "cloud-api" | "filesystem" | "terminal" | "git" | "project-write";

export type ForgeActionRoute = "cloud" | "cloud-vm" | "desktop-local" | "dev-local-web" | "blocked";

export interface RuntimeActionRouteInput {
  readonly actionKind: ForgeActionKind;
  readonly surface: ForgeRuntimeSurface;
  readonly workSource: ForgeWorkSource;
  readonly executionRuntime: ForgeExecutionRuntime;
}

export interface RuntimeActionRouteDecision {
  readonly available: boolean;
  readonly route: ForgeActionRoute;
  readonly requiresPermissionGate: boolean;
  readonly title: string;
  readonly description: string;
}

const LOCAL_ACTIONS = new Set<ForgeActionKind>(["filesystem", "terminal", "git", "project-write"]);

function isLocalWorkSource(workSource: ForgeWorkSource): boolean {
  return workSource === "desktop-local";
}

function isCloudWorkSource(workSource: ForgeWorkSource): boolean {
  return (
    workSource === "cloud" ||
    workSource === "cloud-vm" ||
    workSource === "cmmd" ||
    workSource === "github"
  );
}

export function resolveRuntimeActionRoute(
  input: RuntimeActionRouteInput,
): RuntimeActionRouteDecision {
  if (input.actionKind === "cloud-api") {
    return {
      available: true,
      route: "cloud",
      requiresPermissionGate: false,
      title: "Cloud API route",
      description: "Cloud and Command work routes through authenticated Forge Cloud APIs.",
    };
  }

  if (!LOCAL_ACTIONS.has(input.actionKind)) {
    return {
      available: false,
      route: "blocked",
      requiresPermissionGate: false,
      title: "Unsupported route",
      description: "Forge does not have a runtime route for this action yet.",
    };
  }

  if (isCloudWorkSource(input.workSource)) {
    if (input.workSource === "cloud-vm" && input.executionRuntime === "cloud") {
      return {
        available: true,
        route: "cloud-vm",
        requiresPermissionGate: false,
        title: "Cloud VM route",
        description:
          "Filesystem, terminal, git, and project writes run inside the user's Forge Cloud VM.",
      };
    }

    if (input.executionRuntime === "cloud") {
      return {
        available: true,
        route: "cloud",
        requiresPermissionGate: false,
        title: "Cloud work route",
        description:
          "Cloud-scoped filesystem, terminal, git, and project writes run in the Forge Cloud runtime.",
      };
    }

    return {
      available: false,
      route: "blocked",
      requiresPermissionGate: false,
      title: "Open in Forge Cloud",
      description:
        "Cloud projects must execute inside the Forge Cloud runtime. Local Desktop cannot run cloud project filesystem, terminal, git, or project-write actions.",
    };
  }

  if (!isLocalWorkSource(input.workSource)) {
    return {
      available: false,
      route: "blocked",
      requiresPermissionGate: false,
      title: "Unknown work source",
      description: "Forge cannot safely route local actions for this work source yet.",
    };
  }

  if (input.surface === "desktop-local" || input.executionRuntime === "desktop-local") {
    return {
      available: true,
      route: "desktop-local",
      requiresPermissionGate: false,
      title: "Desktop local route",
      description:
        "Local filesystem, terminal, git, and project writes run through the Desktop local backend.",
    };
  }

  if (input.executionRuntime === "dev-local-web") {
    return {
      available: true,
      route: "dev-local-web",
      requiresPermissionGate: false,
      title: "Dev local route",
      description:
        "Local Web is a development-only projection and routes local actions through the dev server.",
    };
  }

  return {
    available: false,
    route: "blocked",
    requiresPermissionGate: false,
    title: "Switch to Desktop Local",
    description:
      "Local projects must execute in the Desktop Local runtime. Forge Cloud cannot run local filesystem, terminal, git, or project-write actions.",
  };
}
